/**
 * Hooks de lectura del dominio Equipo (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api`. La gestión es
 * global (sin sede en la key).
 */
import { useQuery } from "@tanstack/react-query";

import {
  fetchPermissions,
  fetchRole,
  fetchRoles,
  fetchUser,
  fetchUsersGrouped,
} from "../api";
import type {
  PermissionsCatalog,
  Role,
  RolesList,
  UserDetail,
  UsersGrouped,
} from "../types";
import {
  permissionsQueryKey,
  roleDetailQueryKey,
  rolesListQueryKey,
  userDetailQueryKey,
  usersGroupedQueryKey,
} from "./queryKeys";

/** Lista de usuarios agrupada por sede (`GET /users?group_by=sede`). */
export function useUsersGrouped() {
  return useQuery<UsersGrouped>({
    queryKey: usersGroupedQueryKey(),
    queryFn: fetchUsersGrouped,
    staleTime: 15_000,
  });
}

/** Detalle de un usuario; deshabilitado mientras `username` sea `null`. */
export function useUser(username: string | null) {
  return useQuery<UserDetail>({
    queryKey: userDetailQueryKey(username ?? ""),
    queryFn: () => fetchUser(username as string),
    enabled: username !== null,
    staleTime: 15_000,
  });
}

/** Roles del sistema + personalizados (`GET /roles`). */
export function useRoles() {
  return useQuery<RolesList>({
    queryKey: rolesListQueryKey(),
    queryFn: fetchRoles,
    staleTime: 30_000,
  });
}

/** Detalle de un rol; deshabilitado mientras `roleId` sea `null`. */
export function useRole(roleId: string | null) {
  return useQuery<Role>({
    queryKey: roleDetailQueryKey(roleId ?? ""),
    queryFn: () => fetchRole(roleId as string),
    enabled: roleId !== null,
    staleTime: 15_000,
  });
}

/**
 * Catálogo de permisos (`GET /permissions`). Estable durante la sesión: se cachea
 * largo (la lista no cambia salvo despliegue del backend).
 */
export function usePermissions() {
  return useQuery<PermissionsCatalog>({
    queryKey: permissionsQueryKey(),
    queryFn: fetchPermissions,
    staleTime: Infinity,
  });
}
